import React, { PureComponent } from 'react';
import {
  Modal,
  Switch,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { OptionProps, components as SelectComponents } from 'react-select';
import { connect } from 'react-redux';
import moment from 'moment';
import * as yup from 'yup';
import { v4 } from 'uuid';
import { IAppState } from '../../../../state/reducers';
import {
  IDispatchProps, IProps, IState, IStateProps,
} from './interfaces';
import {
  createLiveAsset,
  updateLiveAssetModalProperties,
  ILiveAssetUpdate,
  restartLiveStream,
  updateAsset,
  ITeamOption,
} from '../../../../state/liveEvents/liveAssetModal';
import VaultButton from '../../../Shared/VaultButton';
import {
  ModalContainer, ModalFormContainer, ModalFormHeader,
  ModalFormLabel, ModalFormControlLabel, ModalFormControl,
  ModalFormListSubheader, ModalFormTextField, ModalFormRow,
  ModalFormGroup, ModalFormButtonContainer, DatePickerStyled,
  TimePickerStyled, TextContainer, ModalRightFormButtonContainer,
  TeamIcon, SwitchContainer, TeamSelect,
} from './styles';
import toast from '../../../../utils/toast';
import { IChannelBooking } from '../../../../api/vault/orgChannels';
import { StreamStatus } from '../../../../enums';
import {
  ILookupOption,
} from '../../../../state/lookups';

/* eslint-disable no-nested-ternary, spaced-comment */

const STEP_IN_SECONDS = 300;
const disabledStartNowStatuses = [
  StreamStatus.Complete.toString(), StreamStatus.Error.toString(), StreamStatus.Restarting.toString(),
  StreamStatus.Live.toString(), StreamStatus.Processing.toString(), StreamStatus.Initializing.toString(),
  // Both already have a host running - one waiting on the feed, one being torn
  // down - so "start now" would fight what is already happening.
  StreamStatus.AwaitingSource.toString(), StreamStatus.SourceEnded.toString(),
];

const DEFAULT_BITRATE = 4000;

const validateFields = [
  'exchangeSport', 'exchangeLeague', 'exchangeSeason', 'exchangeGameType',
  'exchangeHomeTeam', 'exchangeAwayTeam',
  'title', 'url', 'bitrate', 'startTime', 'stopTime', 'securityProfilesSelected',
];

const TeamOption = (props: OptionProps<any, boolean>) => {
  const {
    label,
    isDisabled,
    data: {
      teamData: {
        TeamImage,
      },
    },
  } = props;

  return !isDisabled ? (
    <SelectComponents.Option {...props}>
      <TeamIcon src={TeamImage} />{label}
    </SelectComponents.Option>
  ) : null;
};

class LiveAssetModal extends PureComponent<IProps, IState> {
  componentDidUpdate(): void {
    const { channelSelected, asset } = this.props;

    const listOfAvailableChannels = this.getListOfAvailableChannels();
    if (channelSelected) {
      if (listOfAvailableChannels.length === 0) {
        this.handleUpdate({ name: 'channelSelected', value: null });
      } else if (!listOfAvailableChannels.find((c) => c._id === channelSelected._id)) {
        // if the selected channel is no longer available, remove it.
        this.handleUpdate({ name: 'channelSelected', value: null });
      }
    }

    if (listOfAvailableChannels.length > 0 && asset) {
      const channel = listOfAvailableChannels.find((x) => x.bookedTimes.some((time: IChannelBooking) => time.assetId === asset._id));
      if (channel && !channelSelected) {
        this.handleUpdates([
          { name: 'isRTMPFeed', value: true },
          { name: 'channelSelected', value: channel },
        ]);
      }
    }
  }

  schema = yup.object().shape({
    title: yup.string().required('Title is a required field'),
    url: yup.string().required('Url is a required field'),
    startTime: yup.date().required('Start time is a required field')
      .test(
        'Validate End Time',
        'Start time must be at least 15 minutes into the future.',
        (value) => this.validateStartDate(value),
      ),
    stopTime: yup.date().required('End time is a required field')
      .test(
        'Validate End Time',
        'End time must be greater than the start and current time.',
        (value) => this.validateEndDate(value),
      ),
    securityProfilesSelected: yup.array().of(yup.string()).min(1, 'Security profiles is a required field').required('Security profiles is a required field'),
    // Only validated when the Auto checkbox is off — auto omits the bitrate
    // so the stream host derives it from the source and output settings
    bitrate: yup.number().moreThan(0, 'Bitrate must be greater than 0').required('Bitrate is required (or check Auto)'),
    exchangeSport: yup.string().required('Sport is a required field'),
    exchangeLeague: yup.string().required('League is a required field'),
    exchangeSeason: yup.string().required('Season is a required field'),
    exchangeGameType: yup.string().required('Game Type is a required field'),
    exchangeHomeTeam: yup.string().required('Home Team is a required field'),
    exchangeAwayTeam: yup.string().required('Away Team is a required field'),
  });

  validateStartDate = (value: Date) => {
    if (value > new Date()) {
      return true;
    }
    return false;
  };

  validateEndDate = (value: Date) => {
    const { asset, startTime } = this.props;
    if (moment(startTime).toDate() < value && moment().toDate() < value) {
      return true;
    }
    return false;
  };

  handleCloseModal = async () => {
    const { closeModal } = this.props;
    closeModal();
  };

  handleUpdate = (update: ILiveAssetUpdate) => {
    const { updateLiveAssetModalProperties } = this.props;
    updateLiveAssetModalProperties([update]);
  };

  handleUpdates = (updates: ILiveAssetUpdate[]) => {
    const { updateLiveAssetModalProperties } = this.props;
    updateLiveAssetModalProperties(updates);
  };

  validateSingleField = async (name: string, value: string | string[]) => {
    let { errorList } = this.props;
    let validationValue = value;

    if (!validationValue || validationValue.length === 0) validationValue = undefined;

    let error = '';
    const fieldSchema = yup.reach(this.schema, name) as yup.Schema;
    await fieldSchema.validate(validationValue).catch((err: yup.ValidationError) => {
      error = err.message;
      const existingErrorIndex = errorList.findIndex((e) => e.field === name);
      if (existingErrorIndex !== -1) {
        errorList[existingErrorIndex].message = err.message;
        this.handleUpdate({ name: 'errorList', value: errorList });
      } else {
        this.handleUpdate({ name: 'errorList', value: [...errorList, { field: name, message: err.message }] });
      }
    }).then((validationResponse: unknown) => {
      if (validationResponse) {
        errorList = errorList.filter((x) => x.field !== name);
        this.handleUpdate({ name: 'errorList', value: errorList });
      }
    });
    return error;
  };

  getErrorText = (name: string) => {
    const { errorList } = this.props;
    return (errorList && errorList.find((x) => x.field === name)) ? errorList.find((x) => x.field === name).message : '';
  };

  getError = (name: string) => {
    const { errorList } = this.props;
    return errorList ? errorList.findIndex((x) => x.field === name) !== -1 : false;
  };

  handleInputChange = (e: any) => {
    const { name, value } = e.target;
    this.validateSingleField(name, value);
    this.handleUpdate({ name, value });
  };

  changeStartDateAndTime = (value: any) => {
    this.validateSingleField('startTime', value);
    this.handleUpdate({ name: 'start', value });
  };

  changeStopDateAndTime = (value: any) => {
    this.validateSingleField('stopTime', value);
    this.handleUpdate({ name: 'stop', value });
  };

  changeStartTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { asset, startTime } = this.props;
    const newDateAndTime = this.updateTime(startTime, e.target.value);

    this.changeStartDateAndTime(newDateAndTime);
  };

  changeStopTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { asset, stopTime } = this.props;
    const newDateAndTime = this.updateTime(stopTime, e.target.value);

    this.changeStopDateAndTime(newDateAndTime);
  };

  updateTime = (timeAndDate: string, time: string) => {
    const newTime = moment(time, moment.HTML5_FMT.TIME);
    const newDateAndTime = timeAndDate === null ? moment() : moment(timeAndDate);
    newDateAndTime.hour(newTime.hour());
    newDateAndTime.minutes(newTime.minutes());
    return newDateAndTime;
  };

  handleChangeChannel = async (e: any) => {
    const { value } = e.target;
    this.validateSingleField('url', value.url);
    this.handleUpdate({ name: 'channelSelected', value });
  };

  handleChangeResolution = (e: any) => {
    this.handleUpdate({ name: 'resolution', value: e.target.value });
  };

  handleChangeFps = (e: any) => {
    this.handleUpdate({ name: 'fps', value: e.target.value });
  };

  handleToggleBitrateAuto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    const { bitrate, errorList } = this.props;
    this.handleUpdate({ name: 'bitrateAuto', value: checked });
    if (checked) {
      // Clear any lingering bitrate validation error — the field is ignored in auto mode
      this.handleUpdate({ name: 'errorList', value: errorList.filter((err) => err.field !== 'bitrate') });
    } else if (!bitrate) {
      // Prefill the default when switching to an explicit bitrate
      this.handleUpdate({ name: 'bitrate', value: DEFAULT_BITRATE });
    }
  };

  handleToggleRTMP = async (e: any) => {
    const { channelSelected } = this.props;
    const { checked } = e.target;
    if (checked && channelSelected) this.validateSingleField('url', channelSelected.url);
    this.handleUpdate({ name: 'isRTMPFeed', value: checked });
  };

  handleUpdateSecurityProfiles = async (e: any) => {
    const securityProfiles = e.target.value;
    if (securityProfiles.includes('none')) securityProfiles.splice(securityProfiles.indexOf('none'), 1);
    this.validateSingleField('securityProfilesSelected', securityProfiles);
    this.handleUpdate({ name: 'securityProfilesSelected', value: securityProfiles });
  };

  handleUpdateSport = async (e: any) => this.handleUpdate({ name: 'sportSelected', value: e.target.value });

  handleUpdateHomeTeam = async (e: any) => this.handleUpdate({ name: 'homeTeamSelected', value: e.target.value });

  handleUpdateAwayTeam = async (e: any) => this.handleUpdate({ name: 'awayTeamSelected', value: e.target.value });

  handleUpdateValue = async (e: any, fieldName: string) => {
    let val = e?.target?.value;
    if (fieldName === 'exchangeHomeTeam' || fieldName === 'exchangeAwayTeam') {
      val = e;
    }
    if (validateFields.includes(fieldName)) this.validateSingleField(fieldName, val);
    this.handleUpdate({ name: fieldName, value: val });
  };

  handleSaveAsset = async (startInit = false) => {
    const {
      asset,
      refresh,
      title,
      url,
      bitrate,
      bitrateAuto,
      resolution,
      fps,
      startTime,
      stopTime,
      extendMode,
      restartMode,
      createLiveAsset,
      updateAsset,
      restartLiveStream,
      channelSelected,
      isRTMPFeed,
      securityProfilesSelected,
      sportSelected,
      homeTeamSelected,
      awayTeamSelected,
      isExchangeLiveEvent,
      exchangeLeague,
      exchangeSport,
      exchangeSeason,
      exchangeGameType,
      exchangeHomeTeam,
      exchangeAwayTeam,
    } = this.props;

    let errors = [];
    let scheduleTime = moment(startTime);
    const now = moment(new Date());
    if (startInit) scheduleTime = moment();
    const diff = scheduleTime.diff(now);

    const createMode = !restartMode && !extendMode;
    const editMode = createMode && asset;

    let checkUrl = url;
    if (isRTMPFeed) checkUrl = channelSelected ? channelSelected.url : '';

    if (createMode || editMode) {
      errors.push(await this.validateSingleField('title', title));
      errors.push(await this.validateSingleField('startTime', startTime));
      errors.push(await this.validateSingleField('url', checkUrl));
      if (!bitrateAuto) errors.push(await this.validateSingleField('bitrate', bitrate ? bitrate.toString() : ''));

      if (isExchangeLiveEvent) {
        if (!editMode) {
          errors.push(await this.validateSingleField('exchangeSport', exchangeSport));
          errors.push(await this.validateSingleField('exchangeLeague', exchangeLeague));
          errors.push(await this.validateSingleField('exchangeSeason', exchangeSeason));
          errors.push(await this.validateSingleField('exchangeGameType', exchangeGameType));
          errors.push(await this.validateSingleField('exchangeHomeTeam', exchangeHomeTeam?.value));
          errors.push(await this.validateSingleField('exchangeAwayTeam', exchangeAwayTeam?.value));
        }
      } else {
        errors.push(await this.validateSingleField('securityProfilesSelected', securityProfilesSelected));
      }
    }

    errors.push(await this.validateSingleField('stopTime', stopTime));
    errors = errors.filter((error) => error !== '');

    if (errors.length === 0) {
      if (asset) {
        // note: update from selectedChannel if using rtmp feed
        if (isRTMPFeed) {
          asset._source.streamUrl = channelSelected ? channelSelected.url : url;
          asset._source.streamChannel = channelSelected ? channelSelected.name : null;
          asset._source.channelId = channelSelected ? channelSelected._id : null;
        } else {
          // note: update from provided url if not using rtmp feed
          asset._source.streamUrl = url;
          asset._source.streamChannel = null;
          asset._source.channelId = null;
        }
        asset._source.stop = stopTime;
        if (startInit) {
          asset._source.start = moment().toISOString();
        } else {
          asset._source.start = startTime;
        }
        asset._source.streamBitrate = !bitrateAuto && bitrate ? Number(bitrate) : null;
        asset._source.streamResolution = resolution && resolution !== 'auto' ? resolution : null;
        asset._source.streamFps = fps && fps !== 'auto' ? Number(fps) : null;

        // Add other properties
        asset._source.title = title;
        asset._source.securityProfiles = securityProfilesSelected;
        asset._source.securityProfiles = securityProfilesSelected;
        asset._source.sport = sportSelected;
        asset._source.homeTeam = homeTeamSelected;
        asset._source.awayTeam = awayTeamSelected;

        const editedAsset = { ...asset._source, _id: asset._id };
        if (restartMode) {
          updateAsset(asset._id, editedAsset, async () => {
            refresh();
            restartLiveStream(asset._id, refresh);
          });
        } else {
          updateAsset(asset._id, editedAsset, refresh);
        }
      } else {
        createLiveAsset(startInit, refresh);
        if ((diff < (60 * 1000 * 5))) {
          toast('Stream will start as soon as possible', {
            type: toast.TYPE.WARNING,
          });
        }
      }

      this.handleCloseModal();
    } else {
      console.log('Validation errors, cannot submit form, errors:', errors);
    }
  };

  validateAsset = async () => {
    const {
      startTime, stopTime,
    } = this.props;
    let {
      title, url, securityProfilesSelected,
    } = this.props;

    // This is related to a bug in Yup. null property's do not get validated correctly.
    if (title === null) {
      title = undefined;
    }
    if (url === null) {
      url = undefined;
    }
    if (securityProfilesSelected && securityProfilesSelected.length === 0) {
      securityProfilesSelected = undefined;
    }

    const newAsset = {
      title,
      url,
      stopTime,
      startTime,
      securityProfilesSelected,
    };

    this.schema.validate(newAsset, { abortEarly: false }).catch((errors) => {
      const schemaErrors = errors.inner.map((err: any) => ({ field: err.path, message: err.message }));
      this.handleUpdate({ name: 'errorList', value: schemaErrors });
    }).then((validationResult) => {
      if (validationResult !== undefined) {
        this.handleUpdate({ name: 'errorList', value: [] });
      }
    });
  };

  getListOfAvailableChannels = () => {
    const {
      channelList, asset, stopTime, startTime,
    } = this.props;
    let availableChannels = [];
    if (asset) {
      availableChannels = channelList
        .filter((channel) => channel.bookedTimes.length === 0 || channel.bookedTimes
          .filter((time: IChannelBooking) => this.validateTimes(startTime, time, stopTime) && asset._id !== time.assetId).length === 0);
    } else {
      availableChannels = channelList
        .filter((channel) => channel.bookedTimes.length === 0 || channel.bookedTimes.filter((time: IChannelBooking) => this.validateTimes(startTime, time, stopTime)).length === 0);
    }

    // Sort channels
    const regexNum = /\d+$/; // Gets number at end of string
    const splitAtIndex = (value: string, index: number) => [value.substring(0, index), parseInt(value.substring(index), 10)];
    availableChannels.sort((a, b) => {
      const parseA = a.name.match(regexNum);
      const parseB = b.name.match(regexNum);
      if (parseA && parseB) {
        const [stringA, numA] = splitAtIndex(a.name, parseA.index);
        const [stringB, numB] = splitAtIndex(b.name, parseB.index);

        if (stringA === stringB) {
          return (numA > numB) ? 1 : ((numB > numA) ? -1 : 0);
        }
        return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
      }
      return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
    });

    return availableChannels;
  };

  private validateTimes(startTime: string, time: IChannelBooking, stopTime: string) {
    return (this.IsTimeBetweenValues(startTime, time.start, time.stop)
      || this.IsTimeBetweenValues(stopTime, time.start, time.stop)
      || this.IsTimeBetweenValues(time.start, startTime, stopTime)
      || this.IsTimeBetweenValues(time.stop, startTime, stopTime));
  }

  IsTimeBetweenValues = (timeToCompare: string, startTime: string, stopTime: string) => moment(timeToCompare).isBetween(
    moment(startTime).format('LLL'),
    moment(stopTime).format('LLL'),
  );

  renderExchangeInput = () => {
    const {
      exchangeSport,
      exchangeSeason,
      exchangeLeague,
      exchangeHomeTeam,
      exchangeAwayTeam,
      exchangeGameType,
      exchangeSportOptions,
      exchangeLeagueOptions,
      exchangeTeamOptions,
      exchangeGameTypeOptions,
      exchangeSeasonOptions,
    } = this.props;

    const getTeamInput = (isHome: boolean) => {
      const updateValue = isHome ? 'exchangeHomeTeam' : 'exchangeAwayTeam';
      const value = isHome ? exchangeHomeTeam : exchangeAwayTeam;
      return (
        <ModalFormGroup className={isHome ? 'first' : 'last'}>
          <ModalFormLabel>{isHome ? 'HOME TEAM*' : 'AWAY TEAM*'}</ModalFormLabel>
          <ModalFormControl
            fullWidth
            variant="outlined"
            margin="normal"
            error={this.getError(updateValue)}
          >
            <TeamSelect
              options={exchangeTeamOptions}
              isDisabled={!exchangeLeague || !exchangeSport}
              value={value}
              onChange={(e: any) => this.handleUpdateValue(e, updateValue)}
              className={value ? '' : 'placeholder'}
              components={{
                Option: TeamOption,
                SingleValue: (props: any) => (
                  <SelectComponents.SingleValue {...props}>
                    <TeamIcon src={value.teamData.TeamImage} /> {value.label}
                  </SelectComponents.SingleValue>
                ),
              }}
              menuPortalTarget={document.body}
              menuPlacement="top"
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            />
            <FormHelperText>{this.getErrorText(updateValue)}</FormHelperText>
          </ModalFormControl>
        </ModalFormGroup>
      );
    };

    const getBasicInput = (label: string, placeholder: string, value: string, updateValue: string, isFirst: boolean, options: ILookupOption[]) => (
      <ModalFormGroup className={isFirst ? 'first' : 'last'}>
        <ModalFormLabel>{label}</ModalFormLabel>
        <ModalFormControl
          fullWidth
          variant="outlined"
          margin="normal"
          error={this.getError(updateValue)}
        >
          <Select
            onChange={(e) => this.handleUpdateValue(e, updateValue)}
            value={value || 'none'}
            name={`${updateValue}-select`}
            inputProps={{
              name: `${updateValue}-select`,
              id: `${updateValue}-select`,
            }}
            className={value ? '' : 'placeholder'}
            error={this.getError(updateValue)}
          >
            <MenuItem value="none" key={v4()} disabled>{placeholder}</MenuItem>
            {options.map((option) => <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>)}
          </Select>
          <FormHelperText>{this.getErrorText(updateValue)}</FormHelperText>
        </ModalFormControl>
      </ModalFormGroup>
    );

    return (
      <>
        <ModalFormRow>
          {getBasicInput('SPORT*', 'Sport', exchangeSport, 'exchangeSport', true, exchangeSportOptions)}
          {getBasicInput('LEAGUE*', 'League', exchangeLeague, 'exchangeLeague', false, exchangeLeagueOptions)}
        </ModalFormRow>
        <ModalFormRow>
          {getBasicInput('SEASON*', 'Season', exchangeSeason, 'exchangeSeason', true, exchangeSeasonOptions)}
          {getBasicInput('GAME TYPE*', 'Game Type', exchangeGameType, 'exchangeGameType', false, exchangeGameTypeOptions)}
        </ModalFormRow>
        <ModalFormRow>
          {getTeamInput(true)}
          {getTeamInput(false)}
        </ModalFormRow>
      </>
    );
  };

  renderVaultInput = () => {
    const {
      securityProfiles,
      securityProfilesSelected,
      teams,
      sports,
      sportSelected,
      homeTeamSelected,
      awayTeamSelected,
    } = this.props;
    const securityProfileError = this.getErrorText('securityProfilesSelected');

    const getSecurityProfileOptions = () => {
      const options = [];
      options.push(<MenuItem value="none" key={v4()} disabled>Security Profiles</MenuItem>);
      if (securityProfiles.length > 0) {
        securityProfiles.forEach((groupOption) => {
          options.push(<ModalFormListSubheader className="listSubheader" key={v4()}>{groupOption.label}</ModalFormListSubheader>);
          groupOption.options.forEach((option) => {
            options.push(<MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>);
          });
        });
      }
      return options;
    };

    return (
      <>
        {/* Security Profiles */}
        <ModalFormRow>
          <ModalFormGroup>
            <ModalFormLabel>SECURITY PROFILES*</ModalFormLabel>
            <ModalFormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={securityProfileError !== ''}
            >
              <Select
                onChange={this.handleUpdateSecurityProfiles}
                value={securityProfilesSelected?.length > 0 ? securityProfilesSelected : ['none']}
                name="securityProfiles"
                error={this.getError('securityProfilesSelected')}
                inputProps={{
                  name: 'securityProfileSelect',
                  id: 'securityProfile-select',
                }}
                multiple
                className={securityProfilesSelected?.length > 0 ? '' : 'placeholder'}
              >
                {getSecurityProfileOptions()}
              </Select>
              <FormHelperText>{securityProfileError}</FormHelperText>
            </ModalFormControl>
          </ModalFormGroup>
        </ModalFormRow>
        {/* Sport and Teams */}
        <ModalFormRow>
          <ModalFormGroup>
            <ModalFormLabel>SPORT</ModalFormLabel>
            <ModalFormControl
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <Select
                onChange={this.handleUpdateSport}
                value={sportSelected || 'none'}
                name="sportSelect"
                inputProps={{
                  name: 'sportSelect',
                  id: 'sport-select',
                }}
                className={sportSelected ? '' : 'placeholder'}
              >
                <MenuItem value="none" key={v4()} disabled>Sport</MenuItem>
                {sports.map((sport) => <MenuItem value={sport.value} key={sport.value}>{sport.label}</MenuItem>)}
              </Select>
            </ModalFormControl>
          </ModalFormGroup>
        </ModalFormRow>
        <ModalFormRow>
          <ModalFormGroup className="first">
            <ModalFormLabel>HOME TEAM</ModalFormLabel>
            <ModalFormControl
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <Select
                value={homeTeamSelected || 'none'}
                onChange={this.handleUpdateHomeTeam}
                name="homeTeam"
                inputProps={{
                  name: 'homeTeamSelect',
                  id: 'homeTeam-select',
                }}
                className={homeTeamSelected ? '' : 'placeholder'}
              >
                <MenuItem value="none" key={v4()} disabled>Home Team</MenuItem>
                {teams.map((team) => <MenuItem value={team.value} key={team.value}>{team.label}</MenuItem>)}
              </Select>
            </ModalFormControl>
          </ModalFormGroup>
          <ModalFormGroup className="last">
            <ModalFormLabel>AWAY TEAM</ModalFormLabel>
            <ModalFormControl
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <Select
                value={awayTeamSelected || 'none'}
                onChange={this.handleUpdateAwayTeam}
                name="awayTeam"
                inputProps={{
                  name: 'awayTeamSelect',
                  id: 'awayTeam-select',
                }}
                className={awayTeamSelected ? '' : 'placeholder'}
              >
                <MenuItem value="none" key={v4()} disabled>Away Team</MenuItem>
                {teams.map((team) => <MenuItem value={team.value} key={team.value}>{team.label}</MenuItem>)}
              </Select>
            </ModalFormControl>
          </ModalFormGroup>
        </ModalFormRow>
      </>
    );
  };

  render() {
    const {
      isOpen,
      isButtonDisabled,
      startTime,
      stopTime,
      title,
      url,
      bitrate,
      bitrateAuto,
      streamProvisioned,
      resolution,
      fps,
      asset,
      extendMode,
      restartMode,
      channelSelected,
      channelList,
      isExchangeLiveEvent,
    } = this.props;
    let { isRTMPFeed } = this.props;

    const commonDatePickerProps = {
      disablePast: true,
      disableToolbar: true,
      autoOk: true,
      InputLabelProps: {
        shrink: true,
      },
    };

    const listOfAvailableChannels = this.getListOfAvailableChannels();
    if (listOfAvailableChannels.length === 0) isRTMPFeed = false;

    const commonTimeInputProps = {
      InputLabelProps: {
        shrink: true,
      },
      inputProps: {
        step: STEP_IN_SECONDS,
      },
    };

    const createMode = !restartMode && !extendMode;
    const editMode = createMode && asset;

    let modalTitle = '';
    if (createMode && !editMode) {
      modalTitle = 'Add Live Event';
    } else {
      if (editMode) {
        modalTitle = 'Edit Live Event';
      } else if (restartMode) {
        modalTitle = 'Restart Live Event';
      } else if (extendMode) {
        modalTitle = 'Edit Live Event';
      }

      if (isExchangeLiveEvent) {
        modalTitle += ' - Exchange';
      } else {
        modalTitle += ' - Vault';
      }
    }

    const urlError = this.getErrorText('url');
    const channelError = urlError ? 'Channel is a required field' : '';
    let disableStartNow = false;
    if (asset && asset._source && asset._source.streamStatus && disabledStartNowStatuses.includes(asset._source.streamStatus)) disableStartNow = true;

    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={isOpen}
        onClose={this.handleCloseModal}
        className="_Modal"
      >
        <ModalContainer className="_ModalContainer">
          <ModalFormContainer>
            <ModalFormHeader>
              {modalTitle}
              {createMode && !editMode && (
                <SwitchContainer>
                  <span>Vault</span>
                  <Switch checked={isExchangeLiveEvent} onChange={(e) => this.handleUpdate({ name: 'isExchangeLiveEvent', value: e.target.checked })} />
                  <span>Exchange</span>
                </SwitchContainer>
              )}
            </ModalFormHeader>
            {/* Event data / title input */}
            {(restartMode || extendMode) ? (
              <>
                <TextContainer>Event ID - {asset._id}</TextContainer>
                <TextContainer>Name - {title}</TextContainer>
                <TextContainer>Start Time - {moment(startTime).format('LLL')}</TextContainer>
              </>
            ) : (
              <ModalFormRow>
                <ModalFormGroup>
                  <ModalFormLabel>TITLE*</ModalFormLabel>
                  <ModalFormTextField
                    id="AddNewAssetTitle"
                    name="title"
                    placeholder="Title"
                    variant="outlined"
                    margin="normal"
                    onChange={this.handleInputChange}
                    fullWidth
                    defaultValue={title}
                    required
                    helperText={this.getErrorText('title')}
                    error={this.getError('title')}
                  />
                </ModalFormGroup>
              </ModalFormRow>
            )}
            {/* Start date and time */}
            {createMode && (
              <ModalFormRow>
                <ModalFormGroup className="first FirstDate">
                  <ModalFormLabel>START DATE*</ModalFormLabel>
                  <DatePickerStyled
                    format="MM/dd/yyyy"
                    value={startTime ? moment(startTime).toDate() : null} // This is a Moment | null
                    onChange={(value) => this.changeStartDateAndTime(value ? moment(value as Date) : value)}
                    slotProps={{
                      actionBar: {
                        actions: ['clear', 'today'],
                      },
                    }}
                    {...commonDatePickerProps}
                  />
                </ModalFormGroup>
                <ModalFormGroup className="last">
                  <ModalFormLabel>START TIME*</ModalFormLabel>
                  <TimePickerStyled
                    id="AddNewAssetStartTime"
                    placeholder="Start Time"
                    type="time"
                    variant="outlined"
                    value={moment(startTime).format(moment.HTML5_FMT.TIME)}
                    onChange={this.changeStartTime}
                    {...commonTimeInputProps}
                    helperText={this.getErrorText('startTime')}
                    error={this.getError('startTime')}
                  />
                </ModalFormGroup>
              </ModalFormRow>
            )}
            {/* Stop date and time */}
            <ModalFormRow>
              <ModalFormGroup className="first FirstDate">
                <ModalFormLabel>STOP DATE*</ModalFormLabel>
                <DatePickerStyled
                  format="MM/dd/yyyy"
                  value={stopTime ? moment(stopTime).toDate() : null} // This is a Moment | null
                  onChange={(value) => this.changeStopDateAndTime(value ? moment(value as Date) : value)}
                  minDate={moment(startTime).toDate()}
                  slotProps={{
                    actionBar: {
                      actions: ['clear', 'today'],
                    },
                  }}
                  {...commonDatePickerProps}
                />
              </ModalFormGroup>
              <ModalFormGroup className="last">
                <ModalFormLabel>STOP TIME*</ModalFormLabel>
                <TimePickerStyled
                  id="AddNewAssetStopTime"
                  placeholder="Stop Time"
                  type="time"
                  variant="outlined"
                  value={moment(stopTime).format(moment.HTML5_FMT.TIME)}
                  onChange={this.changeStopTime}
                  {...commonTimeInputProps}
                  helperText={this.getErrorText('stopTime')}
                  error={this.getError('stopTime')}
                />
              </ModalFormGroup>
            </ModalFormRow>
            {/* Channel select/url input */}
            {(restartMode || createMode) && (
              <>
                <ModalFormRow>
                  <ModalFormGroup>
                    {listOfAvailableChannels.length > 0 && (
                      <ModalFormControlLabel
                        control={
                          <Switch checked={isRTMPFeed} onChange={this.handleToggleRTMP} name="urlCheck" />
                        }
                        label={isRTMPFeed ? 'PRE-DEFINED' : 'MANUAL'}
                        labelPlacement="end"
                      />
                    )}
                    {(listOfAvailableChannels.length === 0 && channelList.length > 0) && (
                      <TextContainer>All available channels have been booked for this time.</TextContainer>
                    )}
                  </ModalFormGroup>
                </ModalFormRow>
                <ModalFormRow>
                  <ModalFormGroup>
                    <ModalFormLabel>{!isRTMPFeed ? 'URL*' : 'CHANNELS*'}</ModalFormLabel>
                    {!isRTMPFeed ? (
                      <ModalFormTextField
                        id="AddNewAssetURL"
                        name="url"
                        placeholder="URL"
                        variant="outlined"
                        margin="normal"
                        onChange={this.handleInputChange}
                        defaultValue={url}
                        fullWidth
                        required
                        helperText={this.getErrorText('url')}
                        error={this.getError('url')}
                      />
                    ) : (
                      <ModalFormControl
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        error={channelError !== ''}
                      >
                        <Select
                          value={channelSelected || 'none'}
                          onChange={this.handleChangeChannel}
                          name="url"
                          error={this.getError('url')}
                          inputProps={{
                            name: 'channelSelect',
                            id: 'channel-select',
                          }}
                          className={channelSelected ? '' : 'placeholder'}
                        >
                          <MenuItem value="none" key={v4()} disabled>Channels</MenuItem>
                          {listOfAvailableChannels.map((item) => (<MenuItem key={item.name} value={item}>{item.name} ({item.url})</MenuItem>))}
                        </Select>
                        <FormHelperText>{channelError}</FormHelperText>
                      </ModalFormControl>
                    )}
                  </ModalFormGroup>
                </ModalFormRow>
              </>
            )}
            {(restartMode || createMode) && (
              <>
                <ModalFormRow>
                  <ModalFormGroup>
                    <ModalFormLabel>STREAM BITRATE (KB/S)</ModalFormLabel>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={bitrateAuto}
                          onChange={this.handleToggleBitrateAuto}
                          name="bitrateAuto"
                          size="small"
                          disabled={streamProvisioned}
                          data-auto="BitrateAutoCheckbox"
                        />
                      )}
                      label="Auto (derived from source)"
                    />
                    {!bitrateAuto && (
                      <ModalFormTextField
                        id="StreamBitrate"
                        type="number"
                        name="bitrate"
                        placeholder="Bitrate"
                        variant="outlined"
                        margin="normal"
                        onChange={this.handleInputChange}
                        defaultValue={bitrate}
                        fullWidth
                        required
                        disabled={streamProvisioned}
                        helperText={this.getErrorText('bitrate')}
                        error={this.getError('bitrate')}
                      />
                    )}
                    {streamProvisioned && (
                      <FormHelperText>
                        Output settings are locked once the stream host has been created
                      </FormHelperText>
                    )}
                  </ModalFormGroup>
                </ModalFormRow>
                <ModalFormRow style={{ marginTop: '12px' }}>
                  <ModalFormGroup className="first">
                    <ModalFormLabel>RESOLUTION</ModalFormLabel>
                    <ModalFormControl
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    >
                      <Select
                        value={resolution || 'auto'}
                        onChange={this.handleChangeResolution}
                        disabled={streamProvisioned}
                        inputProps={{
                          name: 'resolution',
                          id: 'resolution-select',
                        }}
                      >
                        <MenuItem value="auto">Auto (match source, up to 1080p)</MenuItem>
                        <MenuItem value="1280x720">720p (1280x720)</MenuItem>
                        <MenuItem value="1920x1080">1080p (1920x1080)</MenuItem>
                      </Select>
                    </ModalFormControl>
                  </ModalFormGroup>
                  <ModalFormGroup className="last">
                    <ModalFormLabel>FRAME RATE</ModalFormLabel>
                    <ModalFormControl
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    >
                      <Select
                        value={fps || 'auto'}
                        onChange={this.handleChangeFps}
                        disabled={streamProvisioned}
                        inputProps={{
                          name: 'fps',
                          id: 'fps-select',
                        }}
                      >
                        <MenuItem value="auto">Auto (match source, up to 60fps)</MenuItem>
                        <MenuItem value="30">30 fps</MenuItem>
                        <MenuItem value="60">60 fps</MenuItem>
                      </Select>
                    </ModalFormControl>
                  </ModalFormGroup>
                </ModalFormRow>
              </>
            )}
            {createMode && (isExchangeLiveEvent ? (!editMode && this.renderExchangeInput()) : this.renderVaultInput())}
            {/* Cancel and save */}
            <ModalFormButtonContainer>
              <VaultButton
                type="button"
                onClick={this.handleCloseModal}
                variant="plainOnWhite"
                data-auto="NewEventCancelBtn"
              >
                Cancel
              </VaultButton>
              <ModalRightFormButtonContainer>
                <Button
                  type="button"
                  disabled={isButtonDisabled || disableStartNow}
                  // need to still fix the initialization
                  onClick={(e: any) => this.handleSaveAsset(true)}
                  variant="outlined"
                  data-auto="StartNowBtn"
                  text-transform="none"
                >
                  Start Now
                </Button>
                <VaultButton
                  type="button"
                  disabled={isButtonDisabled}
                  onClick={(e: any) => this.handleSaveAsset()}
                  variant="primaryOnWhite"
                  data-auto="NewEventSaveBtn"
                >
                  {restartMode ? 'Restart' : 'Save'}
                </VaultButton>
              </ModalRightFormButtonContainer>
            </ModalFormButtonContainer>
          </ModalFormContainer>
        </ModalContainer>
      </Modal>
    );
  }
}

const mapDispatchToProps: IDispatchProps = {
  updateLiveAssetModalProperties,
  createLiveAsset,
  restartLiveStream,
  updateAsset,
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  isButtonDisabled: state.liveAssetModal.isButtonDisabled,
  isRTMPFeed: state.liveAssetModal.isRTMPFeed,
  startTime: state.liveAssetModal.start,
  stopTime: state.liveAssetModal.stop,
  title: state.liveAssetModal.title,
  url: state.liveAssetModal.url,
  bitrate: state.liveAssetModal.bitrate,
  bitrateAuto: state.liveAssetModal.bitrateAuto,
  streamProvisioned: state.liveAssetModal.streamProvisioned,
  resolution: state.liveAssetModal.resolution,
  fps: state.liveAssetModal.fps,
  errorList: state.liveAssetModal.errorList,
  channelList: state.liveAssetModal.channelList,
  channelSelected: state.liveAssetModal.channelSelected,
  securityProfiles: state.liveAssetModal.securityProfiles,
  securityProfilesSelected: state.liveAssetModal.securityProfilesSelected,
  sports: state.liveAssetModal.sports,
  teams: state.liveAssetModal.teams,
  sportSelected: state.liveAssetModal.sportSelected,
  homeTeamSelected: state.liveAssetModal.homeTeamSelected,
  awayTeamSelected: state.liveAssetModal.awayTeamSelected,
  isExchangeLiveEvent: state.liveAssetModal.isExchangeLiveEvent,
  exchangeSport: state.liveAssetModal.exchangeSport,
  exchangeSeason: state.liveAssetModal.exchangeSeason,
  exchangeLeague: state.liveAssetModal.exchangeLeague,
  exchangeHomeTeam: state.liveAssetModal.exchangeHomeTeam,
  exchangeAwayTeam: state.liveAssetModal.exchangeAwayTeam,
  exchangeGameType: state.liveAssetModal.exchangeGameType,
  exchangeSportOptions: state.liveAssetModal.exchangeSportOptions,
  exchangeGameTypeOptions: state.liveAssetModal.exchangeGameTypeOptions,
  exchangeLeagueOptions: state.liveAssetModal.exchangeLeagueOptions,
  exchangeTeamOptions: state.liveAssetModal.exchangeTeamOptions,
  exchangeSeasonOptions: state.liveAssetModal.exchangeSeasonOptions,
});

export default connect(mapStateToProps, mapDispatchToProps)(LiveAssetModal);
